import { ReactElement } from 'react';
import { Box, Button, FormControl, FormLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { eventActions } from '../../redux/slices/event.slice';
import { EEventCreateSteps } from '../../types/@event';

interface IProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const activityOptionsByCategory: Record<string, string[]> = {
  sport: [
    'association football',
    'rugby football',
    'ice hockey',
    'basketball FIBA',
    'field hockey',
    'road run',
    'cross country',
    'parkrun',
  ],
  charity: ['charity activity'],
  general: ['general event'],
};

const contextOptionsByCategory: Record<string, Array<{ value: string; label: string }>> = {
  sport: [
    { value: 'team_hierarchy', label: 'Team hierarchy' },
    { value: 'organisation', label: 'Organisation' },
    { value: 'standalone', label: 'Standalone' },
  ],
  charity: [
    { value: 'organisation', label: 'Organisation' },
    { value: 'standalone', label: 'Standalone' },
  ],
  general: [{ value: 'standalone', label: 'Standalone' }],
};

const EventCreateType = ({ setStep }: IProps): ReactElement => {
  const dispatch = useDispatch();
  const formValues = useSelector((state: RootState) => state.events.eventCreate.eventForm);

  const updateForm = (name: string, value: string): void => {
    let nextActivityKey = formValues.activityKey;
    let nextContextType = formValues.contextType;

    if (name === 'eventCategory') {
      const availableActivities = activityOptionsByCategory[value] || ['general event'];
      const availableContexts = contextOptionsByCategory[value] || [{ value: 'standalone', label: 'Standalone' }];

      nextActivityKey = availableActivities.includes(formValues.activityKey)
        ? formValues.activityKey
        : availableActivities[0];

      nextContextType = availableContexts.some((item) => item.value === formValues.contextType)
        ? formValues.contextType
        : availableContexts[0].value;
    }

    dispatch(
      eventActions.eventCreateForm({
        ...formValues,
        [name]: value,
        activityKey: name === 'eventCategory' ? nextActivityKey : formValues.activityKey,
        contextType: name === 'eventCategory' ? nextContextType : formValues.contextType,
      })
    );
  };

  const handleSelectChange = (event: SelectChangeEvent<string>): void => {
    updateForm(event.target.name, event.target.value);
  };

  const handleNext = (): void => {
    if (formValues.contextType === 'team_hierarchy') {
      setStep(EEventCreateSteps.association);
      return;
    }

    if (formValues.contextType === 'organisation') {
      setStep(EEventCreateSteps.organisation);
      return;
    }

    setStep(EEventCreateSteps.event);
  };

  const categoryOptions = [
    { value: 'sport', label: 'Sport' },
    { value: 'charity', label: 'Charity' },
    { value: 'general', label: 'General' },
  ];

  const activityOptions = activityOptionsByCategory[formValues.eventCategory] || ['general event'];
  const contextOptions = contextOptionsByCategory[formValues.eventCategory] || [
    { value: 'standalone', label: 'Standalone' },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '720px', margin: '0 auto', padding: '48px 24px' }}>
      <Typography sx={{ fontSize: '32px', fontWeight: 700, marginBottom: '16px' }}>
        What kind of event are you creating?
      </Typography>

      <Typography sx={{ fontSize: '16px', marginBottom: '32px', color: '#666' }}>
        Choose the broad event category, the ownership context, and the activity type.
      </Typography>

      <Box sx={{ display: 'grid', gap: '24px' }}>
        <FormControl fullWidth>
          <FormLabel sx={{ marginBottom: '8px' }}>Event category</FormLabel>
          <Select
            name="eventCategory"
            value={formValues.eventCategory}
            onChange={handleSelectChange}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ marginBottom: '8px' }}>Context type</FormLabel>
          <Select
            name="contextType"
            value={formValues.contextType}
            onChange={handleSelectChange}
          >
            {contextOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ marginBottom: '8px' }}>Activity type</FormLabel>
          <Select
            name="activityKey"
            value={formValues.activityKey}
            onChange={handleSelectChange}
          >
            {activityOptions.map((activity) => (
              <MenuItem key={activity} value={activity}>
                {activity}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
        <Button variant="contained" onClick={handleNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default EventCreateType;